const BaseAnnotationTool = cornerstoneTools.importInternal('base/BaseAnnotationTool');
const external = cornerstoneTools.external;
const toolStyle = cornerstoneTools.toolStyle;
const textStyle = cornerstoneTools.textStyle;
const toolColors = cornerstoneTools.toolColors;
const addToolState = cornerstoneTools.addToolState;
const removeToolState = cornerstoneTools.removeToolState;
const getToolState = cornerstoneTools.getToolState;
const moveNewHandle = cornerstoneTools.importInternal('manipulators/moveNewHandle');
const getNewContext = cornerstoneTools.importInternal('drawing/getNewContext');
const draw = cornerstoneTools.importInternal('drawing/draw');
const setShadow = cornerstoneTools.importInternal('drawing/setShadow');
const drawLine = cornerstoneTools.importInternal('drawing/drawLine');
const drawHandles = cornerstoneTools.importInternal('drawing/drawHandles');
const drawLinkedTextBox = cornerstoneTools.importInternal('drawing/drawLinkedTextBox');
const lineSegDistance = cornerstoneTools.importInternal('util/lineSegDistance');
const roundToDecimal = cornerstoneTools.importInternal('util/roundToDecimal');
const EVENTS = cornerstoneTools.EVENTS
const cursors = cornerstoneTools.import('tools/cursors');
const cobbAngleCursor = cursors.cobbAngleCursor;
const triggerEvent = cornerstoneTools.importInternal('util/triggerEvent');
const throttle = cornerstoneTools.importInternal('util/throttle');
const getPixelSpacing = cornerstoneTools.importInternal('util/getPixelSpacing');
const getModule = cornerstoneTools.getModule;

/**
 * @public
 * @class CobbAngleTool
 * @memberof Tools.Annotation
 * @classdesc Tool for measuring the angle between two straight lines.
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class CobbAngleTool extends BaseAnnotationTool {
  constructor(props = {}) {
    const defaultProps = {
      name: 'CobbAngle',
      supportedInteractionTypes: ['Mouse', 'Touch'],
      svgCursor: cobbAngleCursor,
      configuration: {
        drawHandles: true,
        drawHandlesOnHover: false,
        hideHandlesIfMoving: false,
        renderDashed: false,
      },
    };

    super(props, defaultProps);

    this.throttledUpdateCachedStats = throttle(this.updateCachedStats, 10);
  }

  createNewMeasurement(eventData) {
    // Create the measurement data for this tool with the end handle activated
    return {
      visible: true,
      active: true,
      color: undefined,
      invalidated: true,
      value: '',
      handles: {
        start: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
        },
        end: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true,
        },
        textBox: {
          active: false,
          hasMoved: false,
          movesIndependently: false,
          drawnIndependently: true,
          allowedOutsideImage: true,
          hasBoundingBox: true,
        },
      },
    };
  }

  /**
   *
   *
   * @param {*} element
   * @param {*} data
   * @param {*} coords
   * @returns {Boolean}
   */
  pointNearTool(element, data, coords) {
    // if (data.visible === false) {
    //   return false;
    // }

    return (
      lineSegDistance(element, data.handles.start, data.handles.end, coords) <
      25);
  }

  getPair(element, uuid) {
    const toolData = getToolState(element, this.name);
    const index = toolData.data.findIndex(d => d.uuid == uuid);
    if (index % 2 == 0) {
      return toolData.data[index + 1];
    } else {
      return toolData.data[index - 1];
    }
  }

  updateCachedStats(image, element, data) {
    const { rowPixelSpacing, colPixelSpacing } = getPixelSpacing(image);

    const data2 = this.getPair(element, data.uuid);
    if (data2 == null) {
      data.rAngle = "Dibuje otra línea"
      data.invalidated = false;
      return;
    }

    const dx1 =
      (Math.ceil(data.handles.start.x) - Math.ceil(data.handles.end.x)) *
      (colPixelSpacing || 1);
    const dy1 =
      (Math.ceil(data.handles.start.y) - Math.ceil(data.handles.end.y)) *
      (rowPixelSpacing || 1);
    const dx2 =
      (Math.ceil(data2.handles.start.x) - Math.ceil(data2.handles.end.x)) *
      (colPixelSpacing || 1);
    const dy2 =
      (Math.ceil(data2.handles.start.y) - Math.ceil(data2.handles.end.y)) *
      (rowPixelSpacing || 1);

    let angle = Math.acos(
      Math.abs(
        (dx1 * dx2 + dy1 * dy2) /
        (Math.sqrt(dx1 * dx1 + dy1 * dy1) * Math.sqrt(dx2 * dx2 + dy2 * dy2))
      )
    );

    angle *= 180 / Math.PI;

    data.rAngle = roundToDecimal(angle, 2);
    data.invalidated = false;
  }

  renderToolData(evt) {
    const eventData = evt.detail;
    const {
      handleRadius,
      drawHandlesOnHover,
      hideHandlesIfMoving,
      renderDashed,
    } = this.configuration;
    // If we have no toolData for this element, return immediately as there is nothing to do
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }

    // We have tool data for this element - iterate over each one and draw it
    const context = getNewContext(eventData.canvasContext.canvas);

    const lineWidth = toolStyle.getToolWidth();
    const lineDash = getModule('globalConfiguration').configuration.lineDash;
    const font = textStyle.getFont();

    for (let i = 0; i < toolData.data.length; i += 2) {
      const data = toolData.data[i];
      const data2 = toolData.data[i + 1];

      if (data.visible === false || (data2 != null && data2.visible === false)) {
        continue;
      }

      draw(context, context => {
        setShadow(context, this.configuration);

        // Differentiate the color of activation tool
        const color = toolColors.getColorIfActive(data);

        const lineOptions = { color };

        if (renderDashed) {
          lineOptions.lineDash = lineDash;
        }

        drawLine(
          context,
          eventData.element,
          data.handles.start,
          data.handles.end,
          lineOptions
        );

        if (data2 != null) {
          drawLine(
            context,
            eventData.element,
            data2.handles.start,
            data2.handles.end,
            lineOptions
          );


          // const x1 = data.handles.start.x;
          // const x2 = data.handles.end.x;
          // const y1 = data.handles.start.y;
          // const y2 = data.handles.end.y;
          // const slope = (y2 - y1) / (x2 - x1);

          // const x3 = x1;
          // const y3 = y1;

          // const x = (y3 - (y2 + 10)) * slope + x3;
          // const y = -((x - x3) / m) + y3;

          // console.log('slope', slope);

          const l1 = { start: data.handles.start, end: data.handles.end };
          const l2 = { start: data2.handles.start, end: data2.handles.end };
          const { p1, p2 } = getNearestPoints(l1, l2);

          const slope1 = (l1.end.y - l1.start.y) / (l1.end.x - l1.start.x);
          const slope2 = (l2.end.y - l2.start.y) / (l2.end.x - l2.start.x);

          const p3 = { x: 0, y: (p1.y + 50) }
          const p4 = { x: 0, y: (p2.y - 50) }

          p3.x = (p1.y - p3.y) * slope1 + p1.x;
          p4.x = (p2.y - p4.y) * slope2 + p2.x;

          drawLine(
            context,
            eventData.element,
            p1,
            p3,
            lineOptions
          );

          drawLine(
            context,
            eventData.element,
            p2,
            p4,
            lineOptions
          );
        }

        // Draw the handles
        const handleOptions = {
          color,
          handleRadius,
          drawHandlesIfActive: drawHandlesOnHover,
          hideHandlesIfMoving,
        };

        if (this.configuration.drawHandles) {
          drawHandles(context, eventData, data.handles, handleOptions);
          if (data2 != null)
            drawHandles(context, eventData, data2.handles, handleOptions);
        }

        // Draw the text
        context.fillStyle = color;

        const text = data.value;

        if (!data.handles.textBox.hasMoved) {
          const textCoords = {
            x: (data.handles.start.x + data.handles.end.x) / 2,
            y: (data.handles.start.y + data.handles.end.y) / 2 - 10,
          };

          context.font = font;
          data.handles.textBox.x = textCoords.x;
          data.handles.textBox.y = textCoords.y;
        }

        drawLinkedTextBox(
          context,
          eventData.element,
          data.handles.textBox,
          text,
          data.handles,
          textBoxAnchorPoints,
          color,
          lineWidth,
          0,
          true
        );
      });
    }

    function textBoxAnchorPoints(handles) {
      return [handles.start, handles.end];
    }
  }

  addNewMeasurement(evt, interactionType) {
    evt.preventDefault();
    evt.stopPropagation();

    const eventData = evt.detail;

    // Search for incomplete measurements
    const element = evt.detail.element;

    const measurementData = this.createNewMeasurement(eventData);
    addToolState(element, this.name, measurementData);

    const doneMovingCallback = success => {
      // DoneMovingCallback for first measurement.
      if (!success) {
        removeToolState(element, this.name, measurementData);
        return;
      }
      const toolData = getToolState(element, this.name);
      if (toolData != null && measurementData != null) {
        const index = toolData.data.findIndex(d => d.uuid == measurementData.uuid)
        if (index % 2 != 0) {
          const eventType = EVENTS.MEASUREMENT_COMPLETED;
          const eventData = {
            toolName: this.name,
            toolType: this.name, // Deprecation notice: toolType will be replaced by toolName
            element,
            measurementData,
          };
          triggerEvent(element, eventType, eventData);
        }
      }
    };

    // Associate this data with this imageId so we can render it and manipulate it
    external.cornerstone.updateImage(element);

    moveNewHandle(
      eventData,
      this.name,
      measurementData,
      measurementData.handles.end,
      this.options,
      interactionType,
      doneMovingCallback
    );
  }

  onMeasureModified(ev) {
    const { element } = ev.detail;
    const image = external.cornerstone.getEnabledElement(element).image;
    const { rowPixelSpacing, colPixelSpacing } = getPixelSpacing(image);

    if (ev.detail.toolName !== this.name) {
      return;
    }
    const data = ev.detail.measurementData;
    const data2 = this.getPair(element, data.uuid);

    function textBoxText(rAngle, rowPixelSpacing, colPixelSpacing) {
      if (typeof rAngle == 'string') return rAngle;

      const suffix = !rowPixelSpacing || !colPixelSpacing ? ' (isotropic)' : '';
      const str = '00B0'; // Degrees symbol

      return (
        rAngle.toString() + String.fromCharCode(parseInt(str, 16)) + suffix
      );
    }

    // Update textbox stats
    this.updateCachedStats(image, element, data);
    const { rAngle } = data;
    data.value = '';
    if (rAngle != null) {
      data.value = textBoxText(rAngle, rowPixelSpacing, colPixelSpacing);
    }

    if (data2 != null) {
      this.updateCachedStats(image, element, data2);
      const rAngle2 = data2.rAngle;
      data2.value = '';
      if (rAngle2 != null) {
        data2.value = textBoxText(rAngle2, rowPixelSpacing, colPixelSpacing);
      }
    }

  }

  activeCallback(element) {
    this.onMeasureModified = this.onMeasureModified.bind(this);
    element.addEventListener(
      EVENTS.MEASUREMENT_MODIFIED,
      this.onMeasureModified
    );
  }

  passiveCallback(element) {
    this.onMeasureModified = this.onMeasureModified.bind(this);
    element.addEventListener(
      EVENTS.MEASUREMENT_MODIFIED,
      this.onMeasureModified
    );
  }

  enabledCallback(element) {
    element.removeEventListener(
      EVENTS.MEASUREMENT_MODIFIED,
      this.onMeasureModified
    );
  }

  disabledCallback(element) {
    element.removeEventListener(
      EVENTS.MEASUREMENT_MODIFIED,
      this.onMeasureModified
    );
  }
}

function getDistance(p1, p2) {
  var xDiff = p1.x - p2.x;
  var yDiff = p1.y - p2.y;
  return Math.sqrt(xDiff * xDiff + yDiff * yDiff);
}

function getNearestPoints(line1, line2) {
  const start_start = getDistance(line1.start, line2.start);
  const start_end = getDistance(line1.start, line2.end);
  const end_start = getDistance(line1.end, line2.start);
  const end_end = getDistance(line1.end, line2.end);
  switch (Math.min(start_start, start_end, end_start, end_end)) {
    case start_start:
      return { p1: line1.start, p2: line2.start }
    case start_end:
      return { p1: line1.start, p2: line2.end }
    case end_start:
      return { p1: line1.end, p2: line2.start }
    case end_end:
      return { p1: line1.end, p2: line2.end }
  }
}
